/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable jsdoc/require-jsdoc */
import { useMemo, useState } from 'react';
import { useData } from '../context/data.context';
import { usePublicData } from '../context/public-data.context';

// Create stable empty arrays and objects outside the hook
const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT = {};
const NOOP = () => {};
const ASYNC_NOOP = async () => {};

/**
 * Unified data hook that automatically uses the correct context (public or authenticated).
 * Checks sessionStorage to determine if it's a public view.
 * @returns Data context with unified interface.
 */
// eslint-disable-next-line import/prefer-default-export, @typescript-eslint/explicit-function-return-type
export const useUnifiedData = () => {
  // Make isPublicView stable by using state
  const [isPublicView] = useState(() => sessionStorage.getItem('isPublicView') === 'true');

  // For public view, use PublicData context
  const publicData = usePublicData();
  const regularData = useData();

  return useMemo(() => {
    if (isPublicView) {
      // Return a unified interface that matches the regular useData() hook
      return {
        ...publicData,
        // Use actual devices and groupedData from public context
        // Add empty/default values for properties that don't exist in public context
        // Use stable references to prevent unnecessary re-renders
        alertRules: EMPTY_ARRAY,
        allAlerts: EMPTY_ARRAY,
        totalDevices: publicData.devices?.length || 0,
        totalGroups: publicData.groupedData?.length || 0,
        totalGateways: 0,
        clientUsers: EMPTY_ARRAY,
        currentUser: null,
        clientData: null,
        dataRatePerDevice: null,
        clientThresholdSettings: null,
        groupTypes: EMPTY_OBJECT,
        events: EMPTY_ARRAY,
        userConfigData: EMPTY_OBJECT,
        userConfigDataV2: null,
        // Provide no-op functions for methods that don't exist in public context
        fetchAlertRules: ASYNC_NOOP,
        fetchAllDevices: ASYNC_NOOP,
        fetchAllGroups: ASYNC_NOOP,
        fetchUserData: ASYNC_NOOP,
        fetchAlerts: ASYNC_NOOP,
        fetchEvents: ASYNC_NOOP,
        fetchAllUserList: ASYNC_NOOP,
        fetchClientData: ASYNC_NOOP,
        fetchClientThresholdSettings: ASYNC_NOOP,
        refresh: ASYNC_NOOP,
        setParameters: NOOP,
        setUserConfigData: NOOP,
        setUserConfigDataV2: NOOP,
        updateAlertData: NOOP,
        setClientThresholdSettings: NOOP,
        defaultDashboardFilter: publicData.defaultDashboardFilter
      };
    }

    // For authenticated view, use regular Data context
    return {
      ...regularData,
      defaultDashboardFilter: null
    };
  }, [isPublicView, publicData, regularData]);
};
